import { createAction, createReducer, on, props } from '@utils/redux';
import { Action } from '@utils/redux/models';

import {
  FeatureDescriptorDraftFields,
  FeatureDescriptorEditorState,
  JoinConditionDraft,
} from '../models';

export namespace FeatureDescriptorEditorActions {
  export const setDraftField = createAction(
    'SetDraftField',
    props<{
      field: FeatureDescriptorDraftFields | 'join' | 'filter' | 'aggregation';
      value: any;
    }>()
  );

  export namespace Join {
    export const addNewCondition = createAction('Join:AddNewCondition');
    export const setCondition = createAction(
      'Join:SetCondition',
      props<{ condition: Partial<JoinConditionDraft> & { id: number } }>()
    );
    export const patchCondition = createAction(
      'Join:PatchCondition',
      props<{ condition: Partial<JoinConditionDraft> & { id: number } }>()
    );
    export const removeCondition = createAction(
      'Join:RemoveCondition',
      props<{ id: number }>()
    );
  }
}

export const featureDescriptorEditorReducer =
  createReducer<FeatureDescriptorEditorState>(
    on(
      FeatureDescriptorEditorActions.Join.addNewCondition,
      updateDraftField('join', joinState => ({
        ...joinState,
        join_conditions: [
          ...(joinState.join_conditions ?? []),
          { id: Math.random() },
        ],
      }))
    ),
    on(
      FeatureDescriptorEditorActions.Join.setCondition,
      updateDraftField('join', (joinState, action) => ({
        ...joinState,
        join_conditions: joinState.join_conditions.map(
          (condition: JoinConditionDraft) =>
            condition.id === action.condition.id ? action.condition : condition
        ),
      }))
    ),
    on(
      FeatureDescriptorEditorActions.Join.patchCondition,
      updateDraftField('join', (joinState, action) => ({
        ...joinState,
        join_conditions: joinState.join_conditions.map(
          (condition: JoinConditionDraft) =>
            condition.id === action.condition.id
              ? { ...condition, ...action.condition }
              : condition
        ),
      }))
    ),
    on(
      FeatureDescriptorEditorActions.Join.removeCondition,
      updateDraftField('join', (joinState, action) => ({
        ...joinState,
        join_conditions: joinState.join_conditions.filter(
          (condition: JoinConditionDraft) => condition.id !== action.id
        ),
      }))
    ),
    on(FeatureDescriptorEditorActions.setDraftField, setDraftField)
  );

function updateDraftField<A extends Action>(
  field: 'join' | 'filter' | 'aggregation',
  fn: (state: Record<string, any>, action: A) => Record<string, any>
) {
  return function (
    state: FeatureDescriptorEditorState,
    action: A
  ): FeatureDescriptorEditorState {
    return {
      ...state,
      [field]: fn(state[field], action),
    };
  };
}

function setDraftField(
  state: FeatureDescriptorEditorState,
  action: Parameters<typeof FeatureDescriptorEditorActions.setDraftField>[0]
): FeatureDescriptorEditorState {
  const [operation, field] = action.field.split('.') as [
    'join' | 'filter' | 'aggregation',
    string | undefined
  ];

  return {
    ...state,
    [operation]: field
      ? {
          ...state[operation],
          [field]: action.value,
        }
      : action.value,
  };
}
